import React, { useState, useEffect } from "react";
import { Card, InputNumber, Button, Space, Alert, Tabs, Divider } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { updateDirectValues } from "../../store/slice/pricing";

const PRICE_TYPES = [
  { key: "joiningFee", label: "Joining Fee" },
  { key: "proMonthly", label: "Pro Monthly" },
  { key: "premiumMonthly", label: "Premium Monthly" },
  { key: "featuredWeekly", label: "Featured Weekly" },
];

const LOCALITIES = [
  { key: "metro", label: "Metro", color: "#ff4d4f" },
  { key: "urban", label: "Urban", color: "#1890ff" },
  { key: "suburban", label: "Suburban", color: "#722ed1" },
  { key: "rural", label: "Rural", color: "#52c41a" },
];

const TIERS = [
  { key: "high", label: "High Demand" },
  { key: "standard", label: "Standard" },
  { key: "low", label: "Low Demand" },
];

function DirectValuesMatrix() {
  const dispatch = useDispatch();
  const { directValues, isLoading, message } = useSelector((state) => state.pricing);

  const [matrixData, setMatrixData] = useState(directValues);
  const [activeTab, setActiveTab] = useState("joiningFee");
  const [submittingTabs, setSubmittingTabs] = useState({});
  const [successMessages, setSuccessMessages] = useState({});

  useEffect(() => {
    setMatrixData(directValues);
  }, [directValues]);

  const handleCellChange = (priceType, locality, tier, value) => {
    setMatrixData((prev) => ({
      ...prev,
      [priceType]: {
        ...prev[priceType],
        [locality]: {
          ...prev[priceType][locality],
          [tier]: value || 0,
        },
      },
    }));
  };

  const handleSaveTab = async (priceType) => {
    // Validation
    const values = matrixData[priceType];
    const hasZero = Object.values(values).some((locality) =>
      Object.values(locality).some((v) => v <= 0)
    );

    if (hasZero) {
      // Will be handled by Redux error
      return;
    }

    // Transform from { locality: { tier: value } } to { tier: { locality: value } }
    // Backend expects: { high: { metro: 149, urban: 129, ... }, standard: {...}, low: {...} }
    const transformedValues = {
      high: {
        metro: values.metro?.high || 0,
        urban: values.urban?.high || 0,
        suburban: values.suburban?.high || 0,
        rural: values.rural?.high || 0,
      },
      standard: {
        metro: values.metro?.standard || 0,
        urban: values.urban?.standard || 0,
        suburban: values.suburban?.standard || 0,
        rural: values.rural?.standard || 0,
      },
      low: {
        metro: values.metro?.low || 0,
        urban: values.urban?.low || 0,
        suburban: values.suburban?.low || 0,
        rural: values.rural?.low || 0,
      },
    };

    setSubmittingTabs((prev) => ({ ...prev, [priceType]: true }));
    try {
      const resultAction = await dispatch(
        updateDirectValues({
          priceType,
          values: transformedValues,
        })
      );
      if (updateDirectValues.fulfilled.match(resultAction)) {
        setSuccessMessages((prev) => ({
          ...prev,
          [priceType]: true,
        }));
        setTimeout(() => {
          setSuccessMessages((prev) => ({
            ...prev,
            [priceType]: false,
          }));
        }, 3000);
      }
    } finally {
      setSubmittingTabs((prev) => ({ ...prev, [priceType]: false }));
    }
  };

  const handleResetTab = (priceType) => {
    setMatrixData((prev) => ({
      ...prev,
      [priceType]: directValues[priceType],
    }));
  };

  const tabItems = PRICE_TYPES.map((priceType) => ({
    key: priceType.key,
    label: priceType.label,
    children: (
      <PriceTypeMatrix
        priceType={priceType.key}
        values={matrixData[priceType.key]}
        onChange={(locality, tier, value) =>
          handleCellChange(priceType.key, locality, tier, value)
        }
        isLoading={isLoading || submittingTabs[priceType.key]}
        showSuccess={successMessages[priceType.key]}
        onSave={() => handleSaveTab(priceType.key)}
        onReset={() => handleResetTab(priceType.key)}
      />
    ),
  }));

  return (
    <Card
      title="Direct Values Matrix (4×3 Grid)"
      className="mb-6"
      extra={
        <span className="text-xs text-gray-600">
          Set exact prices for each locality × demand tier combination
        </span>
      }
    >
      <Alert
        type="info"
        showIcon
        message="Direct Values Model"
        description="Define independent prices for every combination. No multipliers are applied."
        className="mb-6"
      />

      {message && (
        <Alert
          type="success"
          message="Updated Successfully"
          description={message}
          closable
          className="mb-6"
        />
      )}

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        className="pricing-matrix-tabs"
      />
    </Card>
  );
}

function PriceTypeMatrix({
  priceType,
  values,
  onChange,
  isLoading,
  showSuccess,
  onSave,
  onReset,
}) {
  return (
    <Space direction="vertical" className="w-full" size="large">
      {showSuccess && (
        <Alert
          type="success"
          message="Saved Successfully"
          showIcon
          closable={false}
        />
      )}

      {/* Matrix Grid */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm bg-white">
          <thead>
            <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
              <th className="border border-gray-300 px-4 py-3 text-left font-semibold">
                Locality / Tier
              </th>
              {TIERS.map((tier) => (
                <th
                  key={tier.key}
                  className="border border-gray-300 px-4 py-3 text-center font-semibold"
                >
                  {tier.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {LOCALITIES.map((locality) => (
              <tr key={locality.key} className="hover:bg-gray-50 transition">
                <td
                  className="border border-gray-300 px-4 py-3 font-medium"
                  style={{ borderLeftColor: locality.color, borderLeftWidth: "3px" }}
                >
                  {locality.label}
                </td>
                {TIERS.map((tier) => (
                  <td
                    key={`${locality.key}-${tier.key}`}
                    className="border border-gray-300 px-4 py-3 text-center"
                  >
                    <div className="flex items-center justify-center gap-1">
                      <span className="text-gray-600">₹</span>
                      <InputNumber
                        value={values[locality.key][tier.key]}
                        onChange={(value) =>
                          onChange(locality.key, tier.key, value)
                        }
                        min={1}
                        step={10}
                        precision={0}
                        className="w-24 text-center"
                        disabled={isLoading}
                      />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {LOCALITIES.map((locality) => {
          const localityValues = values[locality.key];
          const minPrice = Math.min(...Object.values(localityValues));
          const maxPrice = Math.max(...Object.values(localityValues));

          return (
            <div
              key={locality.key}
              className="p-3 rounded border-2"
              style={{ borderColor: locality.color, backgroundColor: locality.color + "10" }}
            >
              <p className="text-xs font-semibold text-gray-700 mb-1">
                {locality.label}
              </p>
              <p className="text-xs text-gray-600">
                Min: <span className="font-semibold text-gray-900">₹{minPrice}</span>
              </p>
              <p className="text-xs text-gray-600">
                Max: <span className="font-semibold text-gray-900">₹{maxPrice}</span>
              </p>
            </div>
          );
        })}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 justify-end pt-4 border-t">
        <Button onClick={onReset} disabled={isLoading}>
          Reset
        </Button>
        <Button
          type="primary"
          onClick={onSave}
          loading={isLoading}
          disabled={
            Object.values(values).some((locality) =>
              Object.values(locality).some((v) => v <= 0)
            ) || isLoading
          }
          className="bg-orange-500 hover:bg-orange-600"
        >
          {isLoading ? "Saving..." : "Save Prices"}
        </Button>
      </div>
    </Space>
  );
}

export default DirectValuesMatrix;
